import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Ticket, Plus, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { code: "", kind: "percent", value: 10, min_amount: 0, max_uses: 0, audience: "any", plan_codes: [], module_keys: [], active: true, expires_at: "" };

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get(`${V1}/admin/coupons`).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post(`${V1}/admin/coupons`, { ...form, value: parseFloat(form.value), min_amount: parseFloat(form.min_amount || 0), max_uses: parseInt(form.max_uses || 0), expires_at: form.expires_at || null });
      toast.success("Kupon oluşturuldu");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (code) => {
    if (!window.confirm(`${code} silinsin mi?`)) return;
    await api.delete(`${V1}/admin/coupons/${code}`); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-coupons-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Ticaret</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Ticket className="h-7 w-7 text-primary" /> Kupon Kodları</h1>
        </div>
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-coupon-btn"><Plus className="h-4 w-4" /> Yeni Kupon</Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="text-left p-3">Kod</th><th className="text-left p-3">Tür</th><th className="text-left p-3">Değer</th><th className="text-left p-3">Hedef</th><th className="text-left p-3">Kullanım</th><th className="text-left p-3">Bitiş</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Henüz kupon yok</td></tr>}
            {items.map((c) => (
              <tr key={c.code} className="border-t border-border" data-testid={`coupon-${c.code}`}>
                <td className="p-3 font-mono font-bold">{c.code} <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Kopyalandı"); }}><Copy className="h-3 w-3 inline text-muted-foreground" /></button></td>
                <td className="p-3">{c.kind === "percent" ? "%" : "TL"}</td>
                <td className="p-3">{c.kind === "percent" ? `%${c.value}` : `₺${c.value}`}</td>
                <td className="p-3 text-xs">{c.audience}</td>
                <td className="p-3">{c.used_count}/{c.max_uses || "∞"}</td>
                <td className="p-3 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("tr-TR") : "-"}</td>
                <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => remove(c.code)} data-testid={`del-coupon-${c.code}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Yeni Kupon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kod</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH10" data-testid="coupon-code" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Tür</Label><select className="w-full h-10 px-2 border border-border rounded" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}><option value="percent">Yüzde (%)</option><option value="fixed">Sabit (TL)</option></select></div>
              <div><Label>Değer</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="coupon-value" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Min Tutar (TL)</Label><Input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} /></div>
              <div><Label>Maks Kullanım (0=∞)</Label><Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} /></div>
            </div>
            <div><Label>Hedef Kitle</Label><select className="w-full h-10 px-2 border border-border rounded" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}><option value="any">Herkes</option><option value="parent">Veli</option><option value="student">Öğrenci</option><option value="school">Okul</option></select></div>
            <div><Label>Bitiş Tarihi (ops)</Label><Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Aktif</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button><Button onClick={save} disabled={!form.code || !form.value} data-testid="save-coupon-btn">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
